import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  Search,
  X,
  Users,
  Filter,
  UserPlus,
} from "../components/icons";
import Alert from "../components/Alert";
import { apiClient } from "../lib/api";
import {
  isJerseyNumberMissing,
  normalizePlayers,
  type PlayerApiResponse,
} from "../lib/players";
import {
  buildTeamPayload,
  normalizeTeams,
  type TeamApiResponse,
} from "../lib/teams";
import QuickCreatePlayerModal from "../components/players/QuickCreatePlayerModal";
import { usePositionReferenceOptions } from "../hooks/usePositionReferenceOptions";
import type {
  Team,
  TeamPlayer,
  PlayerData,
  PlayerPosition,
  PaginatedResponse,
  TechnicalStaffMember,
  ImageData,
} from "../types";
import CountrySelect from "../components/CountrySelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useDuplicateCheck } from "../hooks/useDuplicateCheck";
import { useChangeDetection } from "../hooks/useChangeDetection";
import { useLoading } from "../hooks/useLoading";
import DuplicateWarning from "../components/DuplicateWarning";
import ChangeConfirmation from "../components/ChangeConfirmation";
import SimilarRecordsViewer from "../components/SimilarRecordsViewer";
import { useAuthStore } from "../store/authStore";
import Pagination from "../components/Pagination";
import LoadingSpinner from "../components/LoadingSpinner";
import AutocompleteSearch from "../components/AutocompleteSearch";
import ImageUpload from "../components/ImageUpload";
import TeamLogo from "../components/TeamLogo";
import { useFormValidation } from "../hooks/useFormValidation";
import { teamSchema } from "../lib/validationSchemas";
import {
  applyBackendValidationErrors,
  resolveKnownFieldError,
} from "../lib/backendErrorUtils";

// Technical staff roles (stored in English in DB, translated in UI)
// Note: Manager and Technical Director are not included as they are leadership positions
const TECHNICAL_STAFF_ROLES = [
  "Assistant Coach",
  "Goalkeeper Coach",
  "Fitness Coach",
  "Team Doctor",
  "Physiotherapist",
  "Nutritionist",
  "Sports Psychologist",
  "Video Analyst",
  "Scout",
  "Equipment Manager",
  "Other",
] as const;

const ADMIN_LOCALES = ["en", "es"] as const;
type AdminLocale = (typeof ADMIN_LOCALES)[number];

const withTeamFormDefaults = (data?: Partial<Team>): Partial<Team> => {
  const merged = { ...(data ?? {}) };
  const localizedNames: Record<string, string> = {
    ...(merged.i18n_names ?? {}),
  };

  ADMIN_LOCALES.forEach((locale) => {
    localizedNames[locale] = localizedNames[locale] ?? "";
  });

  return {
    ...merged,
    name: merged.name ?? "",
    short_name: merged.short_name ?? "",
    country_name: merged.country_name ?? "",
    gender: merged.gender ?? "male",
    manager: merged.manager ?? {
      name: "",
      country_name: "",
      start_date: null,
    },
    managers: merged.managers ?? [],
    technical_staff: merged.technical_staff ?? [],
    i18n_names: localizedNames,
  };
};

/** Radix `Select.Item` forbids an empty-string value (reserved for
 *  "nothing selected") — same sentinel pattern as `ReferenceValueSelect`. */
const UNSET = "__unset__";

export default function TeamsManager() {
  const { t } = useTranslation(["admin", "common"]);
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<PlayerData[]>([]);
  const { loading, setLoading } = useLoading(true);
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [genderFilter, setGenderFilter] = useState<"male" | "female" | "">("");
  const [showForm, setShowForm] = useState(false);
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Team | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamRoster, setTeamRoster] = useState<TeamPlayer[]>([]);
  const [availablePlayers, setAvailablePlayers] = useState<PlayerData[]>([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [showChangeWarning, setShowChangeWarning] = useState(false);
  const [showSimilarRecords, setShowSimilarRecords] = useState(false);
  const [similarRecords, setSimilarRecords] = useState<any[] | null>(null);
  const [originalFormData, setOriginalFormData] =
    useState<Partial<Team> | null>(null);
  const { checkDuplicates, duplicates, clearDuplicates } = useDuplicateCheck();
  const { detectChanges, changeResult, duplicateResult, clearChangeResult } =
    useChangeDetection();

  // Form validation
  const {
    validateForm,
    validateAndSetFieldError,
    clearErrors,
    getFieldError,
    setFieldError,
  } = useFormValidation<Partial<Team>>(teamSchema, t);

  const formatDeleteGuardError = (detail: unknown): string | null => {
    if (typeof detail !== "string") return null;
    const match = detail.match(/Cannot delete team with (\d+) match(?:es)?/i);
    if (!match) return null;
    const count = Number(match[1]);
    return t("deleteGuards.teamMatches", { count });
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Roster pagination
  const [rosterPage, setRosterPage] = useState(1);
  const [rosterPageSize, setRosterPageSize] = useState(10);
  const [rosterTotalItems, setRosterTotalItems] = useState(0);
  const [isRosterModalLoading, setIsRosterModalLoading] = useState(false);
  const [isRosterPaginationLoading, setIsRosterPaginationLoading] =
    useState(false);
  const [isTeamsPaginationLoading, setIsTeamsPaginationLoading] =
    useState(false);

  const PLAYER_FETCH_PAGE_SIZE = 100;

  const [formData, setFormData] = useState<Partial<Team>>(() =>
    withTeamFormDefaults(),
  );

  const getManagerDraft = () =>
    formData.manager ?? {
      name: "",
      country_name: "",
      start_date: null,
    };

  const [rosterFormData, setRosterFormData] = useState({
    player_id: "",
    jersey_number: 1,
    position: "CM" as PlayerPosition,
    is_active: true,
  });
  const [rosterFormError, setRosterFormError] = useState<string | null>(null);
  const [rosterFieldErrors, setRosterFieldErrors] = useState<
    Record<string, string>
  >({});
  const [rosterPlayerSearch, setRosterPlayerSearch] = useState("");
  const [isPlayerPickerOpen, setIsPlayerPickerOpen] = useState(false);
  const [isAddingPlayerToRoster, setIsAddingPlayerToRoster] = useState(false);
  const [isRosterPositionPickerOpen, setIsRosterPositionPickerOpen] =
    useState(false);
  const [rosterPositionFilter, setRosterPositionFilter] = useState<
    PlayerPosition | "ALL"
  >("ALL");
  const [isRosterFilterOpen, setIsRosterFilterOpen] = useState(false);
  const [showQuickCreatePlayer, setShowQuickCreatePlayer] = useState(false);

  const rosterPositionFormOptions = usePositionReferenceOptions(
    rosterFormData.position,
  );
  const rosterPositionFilterOptions = usePositionReferenceOptions(
    rosterPositionFilter === "ALL" ? null : rosterPositionFilter,
  );
  const flatRosterPositionFormOptions =
    rosterPositionFormOptions.groupOrder.flatMap(
      (group) => rosterPositionFormOptions.grouped[group],
    );
  const flatRosterPositionFilterOptions =
    rosterPositionFilterOptions.groupOrder.flatMap(
      (group) => rosterPositionFilterOptions.grouped[group],
    );

  const clearRosterFieldErrors = () => setRosterFieldErrors({});
  const setRosterFieldError = (field: string, message: string) => {
    setRosterFieldErrors((prev) => ({ ...prev, [field]: message }));
  };
  const clearRosterFieldError = (field: string) => {
    setRosterFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };
  const handleRosterStringError = (detail?: string): boolean => {
    if (!detail) {
      return false;
    }
    console.warn("[TeamsManager] roster string error", detail);
    const normalized = detail.toLowerCase();
    if (normalized.includes("jersey number")) {
      setRosterFieldError("jersey_number", detail);
      setRosterFormError(t("validation.fixErrors"));
      return true;
    }
    if (normalized.includes("already in team")) {
      setRosterFieldError("player_id", detail);
      setRosterFormError(t("validation.fixErrors"));
      return true;
    }
    return false;
  };

  useEffect(() => {
    fetchTeams();
  }, [currentPage, pageSize, genderFilter, searchTerm]);

  useEffect(() => {
    fetchAllPlayers();
  }, []);

  // Monotonic per-invocation id. Guards against a slower, superseded
  // response (e.g. a stale unfiltered page-1 fetch from a StrictMode double
  // mount, or from a request in flight when a search term changes) landing
  // after a newer one and overwriting its result. Incremented synchronously
  // before the first `await` so two invocations fired in the same tick
  // (StrictMode) still get distinct, correctly-ordered ids.
  const fetchTeamsRequestIdRef = useRef(0);

  const fetchTeams = async () => {
    const requestId = ++fetchTeamsRequestIdRef.current;
    setLoading(true);
    try {
      setError(null);
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };

      if (genderFilter) {
        params.gender = genderFilter;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await apiClient.get<PaginatedResponse<TeamApiResponse>>(
        "/teams/",
        { params },
      );
      if (requestId !== fetchTeamsRequestIdRef.current) return; // stale response, ignore
      setTeams(normalizeTeams(response.items));
      setTotalItems(response.total);
      setTotalPages(response.total_pages);
    } catch (err: any) {
      if (requestId !== fetchTeamsRequestIdRef.current) return; // stale error, ignore
      setError(err.response?.data?.detail || t("errorFetchingData"));
    } finally {
      if (requestId === fetchTeamsRequestIdRef.current) {
        setHasInitialLoadCompleted(true);
        setIsTeamsPaginationLoading(false);
        setLoading(false);
      }
    }
  };

  // Monotonic per-invocation id, same pattern as fetchTeamsRequestIdRef.
  // fetchAllPlayers has four call sites (mount effect, handleManageRoster,
  // handleAddPlayerToRoster, handleQuickCreatedPlayer) that all write the
  // same shared `players` state; without this guard a slow mount-triggered
  // sweep (amplified by StrictMode's double mount) can resolve after a fast
  // post-mutation call and clobber a just-created player back out of
  // `players`, which getPlayerName() reads directly (breaks the roster
  // list's player-name display for PMA-ENTITY-020 AC7's "immediately
  // visible" new player until the next unrelated fetch). The returned array
  // is NOT gated: each invocation's own paginated sweep is an accurate
  // snapshot as of when it started, and the three non-mount callers consume
  // it synchronously for their own immediate purpose (building
  // fetchTeamRoster's playerPool), independent of which invocation is
  // globally "latest".
  const fetchAllPlayersRequestIdRef = useRef(0);

  const fetchAllPlayers = async (): Promise<PlayerData[]> => {
    const requestId = ++fetchAllPlayersRequestIdRef.current;
    try {
      const allPlayers: PlayerApiResponse[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await apiClient.get<
          PaginatedResponse<PlayerApiResponse>
        >("/players/", {
          params: { page, page_size: PLAYER_FETCH_PAGE_SIZE },
        });

        allPlayers.push(...response.items);
        totalPages = Math.max(1, response.total_pages || 1);
        page += 1;
      } while (page <= totalPages);

      const normalizedPlayers = normalizePlayers(allPlayers);
      if (requestId === fetchAllPlayersRequestIdRef.current) {
        setPlayers(normalizedPlayers);
      }
      return normalizedPlayers;
    } catch (err: any) {
      console.error("Error fetching players:", err);
      return [];
    }
  };

  // Found while sweeping this file for the same defect class as
  // fetchAllPlayers above: fetchTeamRoster has 4 call sites feeding one
  // shared teamRoster/availablePlayers pair, all normally serialized by the
  // roster modal's own backdrop (a fixed inset-0 overlay blocks clicking a
  // different team's row while the modal is open) — EXCEPT
  // handleCloseRosterModal does not cancel an in-flight fetch. Close the
  // modal while team A's paginated sweep is still in flight, immediately
  // reopen it for team B, and if A's response lands after B's, it clobbers
  // B's roster/available-players with A's data. Same "last response wins"
  // shape, guarded the same way (fetchTeamRosterRequestIdRef, below).
  //
  // The loading flags need a SEPARATE guard from the data guard above.
  // Unlike fetchTeams/fetchPlayers/etc. (which set their single loading flag
  // unconditionally on every invocation, so "latest invocation" and "flag
  // owner" are the same thing), fetchTeamRoster sets isRosterModalLoading or
  // isRosterPaginationLoading CONDITIONALLY on `mode`, and a "refresh"-mode
  // invocation sets neither. Gating the clear on fetchTeamRosterRequestIdRef
  // (the shared "latest for data" counter) broke this: a slow "modal"
  // invocation raises isRosterModalLoading, then a fast "refresh" invocation
  // (e.g. from handleQuickCreatedPlayer, callable while the modal is still
  // loading since its button sits outside the spinner's own ternary) becomes
  // "latest", applies its data correctly, but never clears the modal flag
  // (mode mismatch) — and when the original "modal" invocation finally
  // resolves, ITS clear is skipped because it is no longer "latest", so the
  // flag sticks true until the modal is closed by hand, showing a stale
  // spinner over a roster that already arrived. Each flag therefore tracks
  // its own "owner" requestId (the most recent invocation that actually set
  // it), independent of which invocation is latest for data purposes. This
  // still lets a superseded same-mode invocation defer correctly (e.g.
  // modal-closed-and-reopened-for-a-different-team, above): the newer modal
  // invocation overwrites the owner ref, so the older one's clear is
  // correctly skipped instead of flashing "loaded" mid-fetch for the new
  // team. The shared data guard (fetchTeamRosterRequestIdRef) is untouched
  // by this — a stale invocation still never overwrites fresher roster data.
  const fetchTeamRosterRequestIdRef = useRef(0);
  const rosterModalLoadingOwnerRef = useRef(0);
  const rosterPaginationLoadingOwnerRef = useRef(0);

  const fetchTeamRoster = async (
    teamId: string,
    mode: "modal" | "pagination" | "refresh" = "refresh",
    _pageOverride?: number,
    _pageSizeOverride?: number,
    playerPool?: PlayerData[],
  ) => {
    const requestId = ++fetchTeamRosterRequestIdRef.current;
    if (mode === "modal") {
      rosterModalLoadingOwnerRef.current = requestId;
      setIsRosterModalLoading(true);
    }
    if (mode === "pagination") {
      rosterPaginationLoadingOwnerRef.current = requestId;
      setIsRosterPaginationLoading(true);
    }

    try {
      // Fetch ALL roster items so we sort globally, not per-page
      const allItems: TeamPlayer[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const resp = await apiClient.get<PaginatedResponse<TeamPlayer>>(
          `/teams/${teamId}/players`,
          { params: { page, page_size: 100 } },
        );
        allItems.push(...resp.items);
        totalPages = Math.max(1, resp.total_pages || 1);
        page += 1;
      } while (page <= totalPages);

      if (requestId !== fetchTeamRosterRequestIdRef.current) return; // stale response, ignore

      // A player can be on a roster at most once; de-duplicate defensively so a
      // transient duplicate (e.g. overlapping pages fetched under load) cannot
      // break React key uniqueness in the roster list.
      const uniqueRoster = Array.from(
        new Map(allItems.map((tp) => [tp.player_id, tp])).values(),
      );
      setTeamRoster(uniqueRoster);
      setRosterTotalItems(uniqueRoster.length);

      const rosterPlayerIds = new Set(uniqueRoster.map((tp) => tp.player_id));
      const sourcePlayers = playerPool ?? players;
      const seenCandidates = new Set<string>();
      const filtered = sourcePlayers.filter((p) => {
        if (rosterPlayerIds.has(p.player_id)) return false;
        if (seenCandidates.has(p.player_id)) return false;
        seenCandidates.add(p.player_id);
        return true;
      });
      setAvailablePlayers(filtered);
    } catch (err: any) {
      if (requestId !== fetchTeamRosterRequestIdRef.current) return; // stale error, ignore
      setError(err.response?.data?.detail || t("errorFetchingData"));
    } finally {
      // Gated on each flag's own owner ref, NOT on fetchTeamRosterRequestIdRef
      // — see the comment above the refs for why the two guards must differ.
      if (
        mode === "modal" &&
        requestId === rosterModalLoadingOwnerRef.current
      ) {
        setIsRosterModalLoading(false);
      }
      if (
        mode === "pagination" &&
        requestId === rosterPaginationLoadingOwnerRef.current
      ) {
        setIsRosterPaginationLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm(formData)) {
      setError(t("validation.fixErrors"));
      return;
    }

    const backendPayload = buildTeamPayload(formData);

    // EDIT MODE: Always show changes for confirmation
    if (editingItem && editingItem.team_id) {
      // If we haven't shown the change warning yet, detect changes with duplicate check
      if (!showChangeWarning) {
        // Always clear previous result and detect fresh with combined duplicate check
        clearChangeResult();
        clearDuplicates();

        // Detect changes AND check duplicates in single API call
        const result = await detectChanges(
          "teams",
          editingItem.team_id,
          backendPayload,
          true,
        );

        // Always show change confirmation, even for small changes
        if (result && result.changes.length > 0) {
          setShowChangeWarning(true);
          return;
        } else if (result && result.changes.length === 0) {
          // No changes detected, just save
          setError(t("validation.noChangesDetected"));
          return;
        }
      }

      // After change confirmation, check if duplicates were already detected
      if (showChangeWarning && changeResult) {
        // Duplicates were already checked in combined call, just show them if found
        if (
          duplicateResult &&
          duplicateResult.has_duplicates &&
          !showSimilarRecords
        ) {
          setShowChangeWarning(false);
          setSimilarRecords(duplicateResult.duplicates ?? []);
          setShowSimilarRecords(true);
          return;
        }
      }
    }

    // CREATE MODE: Check for duplicates
    if (!editingItem) {
      const result = await checkDuplicates("teams", backendPayload);

      // Check for 100% duplicates (should be rejected automatically)
      const exactDuplicate = result.duplicates?.find(
        (d) => d.similarity_score === 1.0 || d.match_score === 100,
      );

      if (exactDuplicate) {
        setError(t("validation.exactDuplicateExists"));
        return;
      }

      // Check for similar duplicates (80%+)
      if (result.has_duplicates && !showDuplicateWarning) {
        setShowDuplicateWarning(true);
        return;
      }
    }

    await saveTeam(backendPayload);
  };

  const saveTeam = async (payloadOverride?: Record<string, unknown>) => {
    try {
      const payload = payloadOverride ?? buildTeamPayload(formData);

      if (editingItem) {
        await apiClient.put(`/teams/${editingItem.team_id}`, payload);
      } else {
        await apiClient.post("/teams/", payload);
      }
      await fetchTeams();
      handleCloseForm();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const handled = applyBackendValidationErrors(detail, {
        setFieldError,
        clearErrors,
        translate: t,
      });
      if (handled) {
        setError(t("validation.fixErrors"));
        return;
      }

      const knownFieldError = resolveKnownFieldError(detail);
      if (knownFieldError) {
        setFieldError(knownFieldError.field, t(knownFieldError.translationKey));
        setError(t("validation.fixErrors"));
        return;
      }
      if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(t("errorSavingData"));
      }
    }
  };

  const handleContinueWithDuplicates = async () => {
    setShowDuplicateWarning(false);
    clearDuplicates();
    await saveTeam(buildTeamPayload(formData));
  };

  const handleCancelDuplicates = () => {
    setShowDuplicateWarning(false);
    clearDuplicates();
  };

  const handleConfirmChanges = async () => {
    // User confirmed the changes, proceed to next step (duplicate check)
    setShowChangeWarning(false);
    // Don't clear changeResult here, we need it for the duplicate check

    // Continue with the duplicate check and save flow
    await handleSubmit(new Event("submit") as any);
  };

  const handleCancelChanges = () => {
    // User canceled, reset everything
    setShowChangeWarning(false);
    clearChangeResult();
    setError(null);

    // Revert to original data
    if (originalFormData) {
      setFormData(originalFormData);
    }
  };

  const handleCloseSimilarRecords = () => {
    setShowSimilarRecords(false);
    setSimilarRecords(null);
    clearChangeResult();
  };

  const handleOpenSimilarRecord = (record: any) => {
    setShowSimilarRecords(false);
    setSimilarRecords(null);
    clearChangeResult();
    handleEdit(record);
  };

  const handleLocalizedNameChange = (locale: AdminLocale, value: string) => {
    setFormData((prev) => ({
      ...prev,
      i18n_names: {
        ...(prev.i18n_names ?? {}),
        [locale]: value,
      },
    }));
    validateAndSetFieldError(`i18n_names.${locale}`, value);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await apiClient.delete(`/teams/${id}`);
      await fetchTeams();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const guardMessage = formatDeleteGuardError(detail);
      if (guardMessage) {
        setError(guardMessage);
        return;
      }
      setError(typeof detail === "string" ? detail : t("errorDeletingData"));
    }
  };

  const handleEdit = (item: Team) => {
    setEditingItem(item);
    const formDataToSet = withTeamFormDefaults(item);
    setFormData(formDataToSet);
    setOriginalFormData(formDataToSet); // Store original data for revert
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setShowDuplicateWarning(false);
    setShowChangeWarning(false);
    setShowSimilarRecords(false);
    setOriginalFormData(null);
    clearDuplicates();
    clearChangeResult();
    clearErrors();
    setError(null);
    setFormData(withTeamFormDefaults());
  };

  // Technical staff handlers
  const handleAddStaff = () => {
    const newStaff: TechnicalStaffMember = {
      name: "",
      role: "",
      country_name: "",
      start_date: null,
    };
    setFormData({
      ...formData,
      technical_staff: [...(formData.technical_staff || []), newStaff],
    });
  };

  const handleRemoveStaff = (index: number) => {
    const updatedStaff = [...(formData.technical_staff || [])];
    updatedStaff.splice(index, 1);
    setFormData({ ...formData, technical_staff: updatedStaff });
  };

  const handleUpdateStaff = (
    index: number,
    field: keyof TechnicalStaffMember,
    value: any,
  ) => {
    const updatedStaff = [...(formData.technical_staff || [])];
    updatedStaff[index] = { ...updatedStaff[index], [field]: value };
    setFormData({ ...formData, technical_staff: updatedStaff });
  };

  // Fetch team suggestions for autocomplete
  const fetchTeamSuggestions = async (
    query: string,
  ): Promise<TeamApiResponse[]> => {
    try {
      const response = await apiClient.get<TeamApiResponse[]>(
        "/teams/search/suggestions",
        {
          params: { q: query },
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching team suggestions:", error);
      return [];
    }
  };

  // Handle selecting an existing team from autocomplete
  const handleSelectExistingTeam = (teamData: TeamApiResponse) => {
    const normalizedTeam = normalizeTeams([teamData])[0];
    setEditingItem(normalizedTeam);
    setFormData(withTeamFormDefaults(normalizedTeam));
  };

  const handleManageRoster = async (team: Team) => {
    setSelectedTeam(team);
    clearRosterFieldErrors();
    setRosterFormError(null);
    setShowRosterModal(true);
    setRosterPage(1);
    const latestPlayers = await fetchAllPlayers();
    await fetchTeamRoster(
      team.team_id,
      "modal",
      1,
      rosterPageSize,
      latestPlayers,
    );
  };

  const handleAddPlayerToRoster = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam) return;
    if (isAddingPlayerToRoster) return;

    try {
      setIsAddingPlayerToRoster(true);
      setRosterFormError(null);
      clearRosterFieldErrors();
      const selectedIsAvailable = availablePlayers.some(
        (player) => player.player_id === rosterFormData.player_id,
      );
      if (!selectedIsAvailable) {
        setRosterFieldError("player_id", t("validation.invalidSelection"));
        setRosterFormError(t("validation.fixErrors"));
        return;
      }
      const payload = {
        ...rosterFormData,
        team_id: selectedTeam.team_id,
      };
      await apiClient.post(`/teams/${selectedTeam.team_id}/players`, payload);
      const freshPlayers = await fetchAllPlayers();
      await fetchTeamRoster(
        selectedTeam.team_id,
        "refresh",
        undefined,
        undefined,
        freshPlayers,
      );
      setRosterFormData({
        player_id: "",
        jersey_number: 1,
        position: "CM",
        is_active: true,
      });
      setRosterPlayerSearch("");
      setIsPlayerPickerOpen(false);
      setIsRosterPositionPickerOpen(false);
      setRosterFormError(null);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const handled = applyBackendValidationErrors(detail, {
        setFieldError: setRosterFieldError,
        clearErrors: clearRosterFieldErrors,
        translate: t,
      });
      if (handled) {
        setRosterFormError(t("validation.fixErrors"));
        return;
      }
      if (typeof detail === "string") {
        if (handleRosterStringError(detail)) {
          return;
        }
        setRosterFormError(detail);
      } else {
        setRosterFormError(t("errorSavingData"));
      }
    } finally {
      setIsAddingPlayerToRoster(false);
    }
  };

  const handleRemovePlayerFromRoster = async (playerId: string) => {
    if (!selectedTeam) return;
    if (!window.confirm(t("confirmDelete"))) return;

    try {
      await apiClient.delete(
        `/teams/${selectedTeam.team_id}/players/${playerId}`,
      );
      await fetchTeamRoster(selectedTeam.team_id);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        setRosterFormError(detail);
      } else {
        setRosterFormError(t("errorDeletingData"));
      }
    }
  };

  const handleCloseRosterModal = () => {
    setShowRosterModal(false);
    setSelectedTeam(null);
    setTeamRoster([]);
    setAvailablePlayers([]);
    setIsRosterModalLoading(false);
    setIsRosterPaginationLoading(false);
    setRosterPositionFilter("ALL");
    setIsRosterFilterOpen(false);
    setIsPlayerPickerOpen(false);
    setIsRosterPositionPickerOpen(false);
    setRosterPlayerSearch("");
    clearRosterFieldErrors();
    setRosterFormError(null);
    setShowQuickCreatePlayer(false);
  };

  const handleQuickCreatedPlayer = async () => {
    if (!selectedTeam) return;
    const freshPlayers = await fetchAllPlayers();
    await fetchTeamRoster(
      selectedTeam.team_id,
      "refresh",
      undefined,
      undefined,
      freshPlayers,
    );
  };

  const getPlayerName = (playerId: string): string => {
    const player = players.find((p) => p.player_id === playerId);
    return player?.name || playerId;
  };

  const getPositionBadgeColor = (position: PlayerPosition) => {
    // Goalkeeper
    if (position === "GK") return "bg-purple-100 text-purple-800";
    // Defenders
    if (["CB", "LB", "RB", "LWB", "RWB", "SW"].includes(position)) {
      return "bg-blue-100 text-blue-800";
    }
    // Midfielders
    if (["CDM", "CM", "CAM", "LM", "RM", "LW", "RW"].includes(position)) {
      return "bg-green-100 text-green-800";
    }
    // Forwards
    if (["CF", "ST", "LF", "RF", "SS"].includes(position)) {
      return "bg-red-100 text-red-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  const filteredTeams = teams.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.country_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGender = !genderFilter || item.gender === genderFilter;
    return matchesSearch && matchesGender;
  });

  const filteredRoster = (
    rosterPositionFilter === "ALL"
      ? teamRoster
      : teamRoster.filter((tp) => tp.position === rosterPositionFilter)
  )
    .slice()
    .sort(
      (a, b) => (a.jersey_number ?? Infinity) - (b.jersey_number ?? Infinity),
    );

  // Client-side pagination over the globally-sorted roster
  const clientRosterTotalPages = Math.max(
    1,
    Math.ceil(filteredRoster.length / rosterPageSize),
  );
  const paginatedRoster = filteredRoster.slice(
    (rosterPage - 1) * rosterPageSize,
    rosterPage * rosterPageSize,
  );

  const rosterPlayerCandidates = availablePlayers.filter((player) => {
    const query = rosterPlayerSearch.toLowerCase().trim();
    if (!query) return true;
    return (
      player.name.toLowerCase().includes(query) ||
      player.player_id.toLowerCase().includes(query) ||
      String(player.position || "")
        .toLowerCase()
        .includes(query)
    );
  });

  const selectedRosterPlayer = availablePlayers.find(
    (player) => player.player_id === rosterFormData.player_id,
  );

  if (loading && !hasInitialLoadCompleted) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Shield className="h-8 w-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">{t("teams")}</h1>
        </div>
        {user?.role === "admin" && (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{t("createTeam")}</span>
          </button>
        )}
      </div>

      {error && (
        <Alert tone="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="card grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={t("common:common.search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Select
            value={genderFilter === "" ? UNSET : genderFilter}
            onValueChange={(next) =>
              setGenderFilter(
                (next === UNSET ? "" : next) as "male" | "female" | "",
              )
            }
          >
            <SelectTrigger
              aria-label={t("filterByGender")}
              className="pl-10 w-full"
              data-testid="teams-gender-filter"
            >
              <SelectValue>
                {genderFilter === ""
                  ? `${t("common:common.gender")}: ${t("common:common.filter")}`
                  : genderFilter === "male"
                    ? t("common:common.male")
                    : t("common:common.female")}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNSET}>
                {t("common:common.gender")}: {t("common:common.filter")}
              </SelectItem>
              <SelectItem value="male">{t("common:common.male")}</SelectItem>
              <SelectItem value="female">
                {t("common:common.female")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="card">
        {filteredTeams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <p className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm || genderFilter ? t("noData") : t("noTeams")}
            </p>
          </div>
        ) : (
          <>
            <div className="relative">
              <div className="overflow-x-auto">
                <table
                  className="min-w-full divide-y divide-gray-200"
                  aria-label={t("teams")}
                >
                  <thead className="table-header">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-10"
                      >
                        {t("logo", "Logo")}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {t("common:common.name")}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {t("localizedNames")}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {t("shortName")}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {t("common:common.country")}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {t("common:common.gender")}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {t("manager")}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {t("common:common.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="data-rows">
                    {filteredTeams.map((item) => (
                      <tr key={item._id} className="">
                        <td className="px-4 py-3">
                          <TeamLogo team={item} size="sm" />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="space-y-1">
                            {ADMIN_LOCALES.map((locale) => (
                              <div
                                key={locale}
                                className="flex items-center text-xs"
                              >
                                <span className="font-semibold uppercase text-gray-500 mr-2">
                                  {locale}
                                </span>
                                <span>{item.i18n_names?.[locale] || "-"}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {item.short_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {item.country_name}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              item.gender === "male"
                                ? "bg-primary-100 text-primary-800"
                                : "bg-pink-100 text-pink-800"
                            }`}
                          >
                            {t(item.gender)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {item.manager?.name || "-"}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium space-x-1">
                          <button
                            onClick={() => handleManageRoster(item)}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 transition-colors"
                            title={t("roster")}
                            aria-label={`${t("roster")} ${item.name}`}
                          >
                            <Users className="h-4 w-4" aria-hidden="true" />
                          </button>
                          {user?.role === "admin" && (
                            <>
                              <button
                                onClick={() => handleEdit(item)}
                                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors"
                                title={t("common:common.edit")}
                                aria-label={`${t("common:common.edit")} ${
                                  item.name
                                }`}
                              >
                                <Edit className="h-4 w-4" aria-hidden="true" />
                              </button>
                              <button
                                onClick={() => handleDelete(item.team_id)}
                                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 transition-colors"
                                title={t("common:common.delete")}
                                aria-label={`${t("common:common.delete")} ${
                                  item.name
                                }`}
                              >
                                <Trash2
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={(page) => {
                  if (page === currentPage) return;
                  setIsTeamsPaginationLoading(true);
                  setCurrentPage(page);
                }}
                onPageSizeChange={(newPageSize) => {
                  if (newPageSize === pageSize && currentPage === 1) return;
                  setIsTeamsPaginationLoading(true);
                  setPageSize(newPageSize);
                  setCurrentPage(1);
                }}
              />
              {isTeamsPaginationLoading && (
                <div
                  className="absolute inset-0 bg-white/70 flex items-center justify-center z-10"
                  data-testid="teams-pagination-loading"
                >
                  <LoadingSpinner size="sm" />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Team Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-form-modal-title"
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2
                id="team-form-modal-title"
                className="text-xl font-semibold text-gray-900"
              >
                {editingItem ? t("common:common.edit") : t("createTeam")}
              </h2>
              <button
                onClick={handleCloseForm}
                aria-label={t("common:common.close", "Close dialog")}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 transition-colors"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <form
              id="team-form"
              onSubmit={handleSubmit}
              className="p-6 space-y-4 max-h-[calc(90vh-8rem)] overflow-y-auto"
            >
              {error && (
                <Alert tone="error" onDismiss={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {showChangeWarning &&
                changeResult &&
                changeResult.changes.length > 0 && (
                  <ChangeConfirmation
                    changePercentage={changeResult.change_percentage}
                    changes={changeResult.changes}
                    onConfirm={handleConfirmChanges}
                    onCancel={handleCancelChanges}
                  />
                )}
              {showDuplicateWarning &&
                duplicates &&
                duplicates.has_duplicates && (
                  <DuplicateWarning
                    duplicates={duplicates.duplicates}
                    onContinue={handleContinueWithDuplicates}
                    onCancel={handleCancelDuplicates}
                    entityType="team"
                    onViewSimilarRecords={
                      duplicates.duplicates.length > 0
                        ? () => {
                            setSimilarRecords(duplicates.duplicates);
                            setShowSimilarRecords(true);
                          }
                        : undefined
                    }
                  />
                )}
              <div className="flex items-start gap-4">
                <ImageUpload
                  kind="team-logo"
                  entityId={editingItem?.team_id}
                  url={formData.logo_url}
                  legacyImage={
                    formData.logo_image as ImageData | null | undefined
                  }
                  onUploaded={(logoUrl) =>
                    setFormData({ ...formData, logo_url: logoUrl })
                  }
                  onDeleted={() =>
                    setFormData({ ...formData, logo_url: undefined })
                  }
                  canEdit={user?.role === "admin"}
                  label={t("logo", "Logo")}
                  size={80}
                />
                <div className="flex-1">
                  <AutocompleteSearch<TeamApiResponse>
                    name="name"
                    value={formData.name || ""}
                    onChange={(value) => {
                      setFormData({ ...formData, name: value });
                      validateAndSetFieldError("name", value);
                    }}
                    onSelectSuggestion={handleSelectExistingTeam}
                    fetchSuggestions={fetchTeamSuggestions}
                    getDisplayText={(team) => team.name}
                    getSecondaryText={(team) =>
                      `${team.short_name} • ${team.country_name} • ${team.gender}`
                    }
                    placeholder={t("teamNamePlaceholder")}
                    label={t("common:common.name")}
                    required
                    minChars={3}
                  />
                  {getFieldError("name") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("name")}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {t("localizedNames")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("localizedNamesDescription")}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ADMIN_LOCALES.map((locale) => {
                    const fieldKey = `i18n_names.${locale}`;
                    const inputId = `team-i18n-name-${locale}`;
                    return (
                      <div key={locale}>
                        <label
                          htmlFor={inputId}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          {locale === "en"
                            ? t("localizedNameEn")
                            : t("localizedNameEs")}
                        </label>
                        <input
                          id={inputId}
                          type="text"
                          maxLength={100}
                          value={formData.i18n_names?.[locale] ?? ""}
                          onChange={(e) =>
                            handleLocalizedNameChange(locale, e.target.value)
                          }
                          onBlur={(e) =>
                            validateAndSetFieldError(fieldKey, e.target.value)
                          }
                          className={`input w-full ${
                            getFieldError(fieldKey) ? "border-red-500" : ""
                          }`}
                        />
                        {getFieldError(fieldKey) && (
                          <p className="mt-1 text-sm text-red-600">
                            {getFieldError(fieldKey)}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label
                    htmlFor="team-short-name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("shortName")} *
                  </label>
                  <input
                    id="team-short-name"
                    type="text"
                    required
                    value={formData.short_name}
                    onChange={(e) => {
                      setFormData({ ...formData, short_name: e.target.value });
                      validateAndSetFieldError("short_name", e.target.value);
                    }}
                    onBlur={(e) =>
                      validateAndSetFieldError("short_name", e.target.value)
                    }
                    className={`input w-full ${
                      getFieldError("short_name") ? "border-red-500" : ""
                    }`}
                  />
                  {getFieldError("short_name") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("short_name")}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("common:common.country")} *
                  </label>
                  <CountrySelect
                    required
                    aria-label={t("common:common.country")}
                    value={formData.country_name || ""}
                    onChange={(next) => {
                      setFormData({
                        ...formData,
                        country_name: next,
                      });
                      validateAndSetFieldError("country_name", next);
                    }}
                    onBlur={() =>
                      validateAndSetFieldError(
                        "country_name",
                        formData.country_name || "",
                      )
                    }
                    placeholder={t("common:common.selectCountry")}
                    className={
                      getFieldError("country_name") ? "border-red-500" : ""
                    }
                  />
                  {getFieldError("country_name") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("country_name")}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="team-gender"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("common:common.gender")} *
                  </label>
                  <Select
                    value={formData.gender}
                    onValueChange={(next) => {
                      const value = next as "male" | "female";
                      setFormData({ ...formData, gender: value });
                      validateAndSetFieldError("gender", value);
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        validateAndSetFieldError("gender", formData.gender);
                      }
                    }}
                    required
                  >
                    <SelectTrigger
                      id="team-gender"
                      className={
                        getFieldError("gender") ? "border-red-500" : ""
                      }
                    >
                      <SelectValue>
                        {formData.gender === "male"
                          ? t("common:common.male")
                          : t("common:common.female")}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">
                        {t("common:common.male")}
                      </SelectItem>
                      <SelectItem value="female">
                        {t("common:common.female")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {getFieldError("gender") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("gender")}
                    </p>
                  )}
                </div>
              </div>
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3">{t("manager")}</h3>
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="team-manager-name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("managerName")} *
                    </label>
                    <input
                      id="team-manager-name"
                      type="text"
                      required
                      value={formData.manager?.name || ""}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          manager: {
                            ...getManagerDraft(),
                            name: e.target.value,
                          },
                        });
                        validateAndSetFieldError(
                          "manager.name",
                          e.target.value,
                        );
                      }}
                      onBlur={(e) =>
                        validateAndSetFieldError("manager.name", e.target.value)
                      }
                      className={`input w-full ${
                        getFieldError("manager.name") ? "border-red-500" : ""
                      }`}
                    />
                    {getFieldError("manager.name") && (
                      <p className="mt-1 text-sm text-red-600">
                        {getFieldError("manager.name")}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="team-manager-nationality"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("managerNationality")}
                    </label>
                    <CountrySelect
                      id="team-manager-nationality"
                      value={formData.manager?.country_name || ""}
                      onChange={(next) => {
                        setFormData({
                          ...formData,
                          manager: {
                            ...getManagerDraft(),
                            country_name: next,
                          },
                        });
                        validateAndSetFieldError(
                          "manager.country_name",
                          next || undefined,
                        );
                      }}
                      onBlur={() =>
                        validateAndSetFieldError(
                          "manager.country_name",
                          formData.manager?.country_name || undefined,
                        )
                      }
                      placeholder={t("common:common.selectCountry")}
                      className={
                        getFieldError("manager.country_name")
                          ? "border-red-500"
                          : ""
                      }
                    />
                    {getFieldError("manager.country_name") && (
                      <p className="mt-1 text-sm text-red-600">
                        {getFieldError("manager.country_name")}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Technical Staff Section */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">
                    {t("technicalStaff")}
                  </h3>
                  <button
                    type="button"
                    onClick={handleAddStaff}
                    className="btn btn-sm btn-secondary flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" /> {t("addStaff")}
                  </button>
                </div>
                {formData.technical_staff &&
                  formData.technical_staff.length > 0 && (
                    <div className="space-y-4">
                      {formData.technical_staff.map((staff, index) => (
                        <div
                          key={index}
                          className="border rounded-lg p-4 bg-gray-50"
                        >
                          <div className="flex justify-between items-center mb-3">
                            <span className="font-medium text-sm text-gray-700">
                              {t("staffMember")} #{index + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveStaff(index)}
                              aria-label={`${t("common:common.delete")} ${t(
                                "staffMember",
                              )} ${index + 1}`}
                              className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </div>
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label
                                  htmlFor={`staff-name-${index}`}
                                  className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                  {t("common:common.name")}
                                </label>
                                <input
                                  id={`staff-name-${index}`}
                                  type="text"
                                  value={staff.name || ""}
                                  onChange={(e) =>
                                    handleUpdateStaff(
                                      index,
                                      "name",
                                      e.target.value,
                                    )
                                  }
                                  className="input w-full"
                                />
                              </div>
                              <div>
                                <label
                                  htmlFor={`staff-role-${index}`}
                                  className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                  {t("role")}
                                </label>
                                <Select
                                  value={staff.role || UNSET}
                                  onValueChange={(next) =>
                                    handleUpdateStaff(
                                      index,
                                      "role",
                                      next === UNSET ? "" : next,
                                    )
                                  }
                                >
                                  <SelectTrigger id={`staff-role-${index}`}>
                                    <SelectValue
                                      placeholder={t("rolePlaceholder")}
                                    >
                                      {staff.role
                                        ? t(`technicalStaffRoles.${staff.role}`)
                                        : undefined}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={UNSET}>
                                      {t("rolePlaceholder")}
                                    </SelectItem>
                                    {TECHNICAL_STAFF_ROLES.map((role) => (
                                      <SelectItem key={role} value={role}>
                                        {t(`technicalStaffRoles.${role}`)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div>
                              <label
                                htmlFor={`staff-country-${index}`}
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                {t("common:common.country")}
                              </label>
                              <CountrySelect
                                id={`staff-country-${index}`}
                                value={staff.country_name || ""}
                                onChange={(next) =>
                                  handleUpdateStaff(index, "country_name", next)
                                }
                                placeholder={t("common:common.selectCountry")}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 sticky bottom-0 bg-white border-t border-gray-200 -mx-6 px-6 py-4">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="btn btn-secondary"
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={showChangeWarning || showSimilarRecords}
                >
                  {t("save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Roster Management Modal */}
      {showRosterModal && selectedTeam && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          data-testid="roster-modal"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="roster-modal-title"
            className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[94vh] min-h-[min(88vh,44rem)] flex flex-col overflow-hidden"
            data-testid="roster-modal-panel"
          >
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2
                id="roster-modal-title"
                className="text-xl font-semibold text-gray-900"
              >
                {t("roster")} - {selectedTeam.name}
              </h2>
              <button
                onClick={handleCloseRosterModal}
                aria-label={t("common:common.close", "Close dialog")}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 transition-colors"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.95fr)] gap-6 min-h-full">
                {/* Current Roster */}
                <div className="min-h-0">
                  <h3 className="text-lg font-semibold mb-4">
                    {t("starters")} ({rosterTotalItems})
                  </h3>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("position")}
                    </label>
                    <div
                      className="relative"
                      data-testid="roster-filter-position"
                    >
                      <button
                        type="button"
                        data-testid="roster-filter-position-toggle"
                        onClick={() => setIsRosterFilterOpen((prev) => !prev)}
                        className="input w-full text-left flex items-center justify-between"
                      >
                        <span>
                          {rosterPositionFilter === "ALL"
                            ? t("allPositions")
                            : rosterPositionFilterOptions.labelFor(
                                rosterPositionFilter,
                              )}
                        </span>
                        <span className="text-gray-500">▾</span>
                      </button>
                      {isRosterFilterOpen && (
                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                          <button
                            type="button"
                            data-testid="roster-filter-position-option-ALL"
                            onClick={() => {
                              setRosterPositionFilter("ALL");
                              setIsRosterFilterOpen(false);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50"
                          >
                            {t("allPositions")}
                          </button>
                          {flatRosterPositionFilterOptions.map((opt) => (
                            <button
                              key={`filter-${opt.code}`}
                              type="button"
                              data-testid={`roster-filter-position-option-${opt.code}`}
                              onClick={() => {
                                setRosterPositionFilter(opt.code);
                                setIsRosterFilterOpen(false);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50"
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {isRosterModalLoading ? (
                    <div
                      className="flex items-center justify-center py-12"
                      data-testid="roster-modal-loading"
                    >
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : filteredRoster.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                      <p className="text-sm text-gray-500">
                        {rosterPositionFilter === "ALL"
                          ? t("noPlayers")
                          : t("noData")}
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="space-y-2">
                        {paginatedRoster.map((tp) => (
                          <div
                            key={tp.player_id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                {isJerseyNumberMissing({
                                  jersey_number: tp.jersey_number,
                                }) ? (
                                  <span
                                    className="flex items-baseline gap-1"
                                    title={t("rosterMissingJerseyHint")}
                                  >
                                    <span
                                      className="font-semibold text-lg text-amber-600"
                                      data-testid={`roster-jersey-missing-${tp.player_id}`}
                                    >
                                      #—
                                    </span>
                                    <span className="text-[10px] text-amber-600">
                                      {t("rosterMissingJerseyHint")}
                                    </span>
                                  </span>
                                ) : (
                                  <span
                                    className="font-semibold text-lg"
                                    data-testid={`roster-jersey-${tp.player_id}`}
                                  >
                                    #{tp.jersey_number}
                                  </span>
                                )}
                                <span className="font-medium">
                                  {getPlayerName(tp.player_id)}
                                </span>
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${getPositionBadgeColor(
                                    tp.position,
                                  )}`}
                                >
                                  {rosterPositionFormOptions.labelFor(
                                    tp.position,
                                  )}
                                </span>
                                {!tp.is_active && (
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-gray-300 text-gray-700">
                                    {t("inactive")}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() =>
                                handleRemovePlayerFromRoster(tp.player_id)
                              }
                              className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 transition-colors"
                              title={t("removeFromRoster")}
                              aria-label={`${t(
                                "removeFromRoster",
                              )} ${getPlayerName(tp.player_id)}`}
                            >
                              <Trash2 className="h-5 w-5" aria-hidden="true" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4">
                        <Pagination
                          currentPage={rosterPage}
                          totalPages={clientRosterTotalPages}
                          totalItems={filteredRoster.length}
                          pageSize={rosterPageSize}
                          onPageChange={(page) => {
                            if (page === rosterPage) return;
                            setRosterPage(page);
                          }}
                          onPageSizeChange={(newPageSize) => {
                            if (
                              newPageSize === rosterPageSize &&
                              rosterPage === 1
                            ) {
                              return;
                            }
                            setRosterPageSize(newPageSize);
                            setRosterPage(1);
                          }}
                        />
                      </div>
                      {isRosterPaginationLoading && (
                        <div
                          className="absolute inset-0 bg-white/70 flex items-center justify-center z-10"
                          data-testid="roster-pagination-loading"
                        >
                          <LoadingSpinner size="sm" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Add Player Form */}
                <div className="min-h-0 rounded-xl border border-gray-200 bg-gray-50/70 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      {t("addToRoster")}
                    </h3>
                    <div className="flex items-center gap-2">
                      {selectedTeam && (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/admin/roster-import?team=${selectedTeam.team_id}`,
                            )
                          }
                          className="btn btn-sm btn-secondary flex items-center gap-2"
                          data-testid="open-roster-import-from-team"
                          title={t("rosterImport.title")}
                        >
                          <Users className="h-4 w-4" aria-hidden="true" />
                          {t("rosterImport.title")}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowQuickCreatePlayer(true)}
                        className="btn btn-sm btn-secondary flex items-center gap-2"
                        data-testid="open-quick-create-player"
                      >
                        <UserPlus className="h-4 w-4" aria-hidden="true" />
                        {t("quickCreatePlayer.addNewPlayer")}
                      </button>
                    </div>
                  </div>
                  <form
                    onSubmit={handleAddPlayerToRoster}
                    className="space-y-4"
                  >
                    {rosterFormError && (
                      <Alert
                        tone="error"
                        onDismiss={() => setRosterFormError(null)}
                      >
                        {rosterFormError}
                      </Alert>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("players")} *
                      </label>
                      <div
                        className="space-y-2"
                        data-testid="roster-player-picker"
                      >
                        <button
                          type="button"
                          data-testid="roster-player-picker-toggle"
                          onClick={() => setIsPlayerPickerOpen((prev) => !prev)}
                          className={`input w-full text-left flex items-center justify-between ${
                            rosterFieldErrors.player_id ? "border-red-500" : ""
                          }`}
                        >
                          <span>
                            {selectedRosterPlayer
                              ? `${selectedRosterPlayer.name} - ${rosterPositionFormOptions.labelFor(
                                  selectedRosterPlayer.position,
                                )}`
                              : t("selectPlayers")}
                          </span>
                          <span className="text-gray-500">▾</span>
                        </button>
                        {isPlayerPickerOpen && (
                          <div
                            className="border border-gray-200 rounded-md p-2 space-y-2"
                            data-testid="roster-player-picker-panel"
                          >
                            <input
                              type="text"
                              value={rosterPlayerSearch}
                              onChange={(e) =>
                                setRosterPlayerSearch(e.target.value)
                              }
                              placeholder={t("search")}
                              data-testid="roster-player-search"
                              className="input w-full"
                            />
                            <div className="max-h-52 overflow-auto border border-gray-100 rounded-md">
                              {rosterPlayerCandidates.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-gray-500">
                                  {t("noData")}
                                </div>
                              ) : (
                                rosterPlayerCandidates.map((player) => (
                                  <button
                                    key={player.player_id}
                                    type="button"
                                    data-testid={`roster-available-player-option-${player.player_id}`}
                                    onClick={() => {
                                      clearRosterFieldError("player_id");
                                      setRosterFormData({
                                        ...rosterFormData,
                                        player_id: player.player_id,
                                      });
                                      setRosterPlayerSearch("");
                                      setIsPlayerPickerOpen(false);
                                    }}
                                    className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                                  >
                                    {player.name} -{" "}
                                    {rosterPositionFormOptions.labelFor(
                                      player.position,
                                    )}
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {rosterFieldErrors.player_id && (
                        <p className="mt-1 text-sm text-red-600">
                          {rosterFieldErrors.player_id}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="roster-jersey-number"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          {t("jerseyNumber")} *
                        </label>
                        <input
                          id="roster-jersey-number"
                          type="number"
                          required
                          min="1"
                          max="99"
                          value={rosterFormData.jersey_number}
                          onChange={(e) => {
                            clearRosterFieldError("jersey_number");
                            setRosterFormData({
                              ...rosterFormData,
                              jersey_number: parseInt(e.target.value),
                            });
                          }}
                          className={`input w-full ${
                            rosterFieldErrors.jersey_number
                              ? "border-red-500"
                              : ""
                          }`}
                        />
                        {rosterFieldErrors.jersey_number && (
                          <p className="mt-1 text-sm text-red-600">
                            {rosterFieldErrors.jersey_number}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("position")} *
                        </label>
                        <div
                          className="relative"
                          data-testid="roster-position-picker"
                        >
                          <button
                            type="button"
                            data-testid="roster-position-toggle"
                            onClick={() =>
                              setIsRosterPositionPickerOpen((prev) => !prev)
                            }
                            className={`input w-full text-left flex items-center justify-between ${
                              rosterFieldErrors.position ? "border-red-500" : ""
                            }`}
                          >
                            <span>
                              {rosterPositionFormOptions.labelFor(
                                rosterFormData.position,
                              )}
                            </span>
                            <span className="text-gray-500">▾</span>
                          </button>
                          {isRosterPositionPickerOpen && (
                            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                              {flatRosterPositionFormOptions.map((opt) => (
                                <button
                                  key={`roster-position-${opt.code}`}
                                  type="button"
                                  data-testid={`roster-position-option-${opt.code}`}
                                  onClick={() => {
                                    clearRosterFieldError("position");
                                    setRosterFormData({
                                      ...rosterFormData,
                                      position: opt.code,
                                    });
                                    setIsRosterPositionPickerOpen(false);
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {rosterFieldErrors.position && (
                          <p className="mt-1 text-sm text-red-600">
                            {rosterFieldErrors.position}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={rosterFormData.is_active}
                        onChange={(e) =>
                          setRosterFormData({
                            ...rosterFormData,
                            is_active: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="is_active"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        {t("active")}
                      </label>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary w-full"
                      disabled={isAddingPlayerToRoster}
                    >
                      {isAddingPlayerToRoster ? (
                        <span className="inline-block animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                      ) : (
                        <Plus className="h-5 w-5 inline mr-2" />
                      )}
                      {t("addToRoster")}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Similar Records Viewer Modal */}
      {showSimilarRecords && similarRecords && similarRecords.length > 0 && (
        <SimilarRecordsViewer
          records={similarRecords}
          currentData={formData}
          entityType="team"
          onClose={handleCloseSimilarRecords}
          onOpenRecord={handleOpenSimilarRecord}
        />
      )}

      {/* Quick-create Player Modal (EPIC-08 Phase 2) */}
      {selectedTeam && (
        <QuickCreatePlayerModal
          teamId={selectedTeam.team_id}
          teamName={selectedTeam.name}
          isOpen={showQuickCreatePlayer}
          onClose={() => setShowQuickCreatePlayer(false)}
          onCreated={handleQuickCreatedPlayer}
        />
      )}
    </div>
  );
}
